import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SectionCard } from "@/components/shared/PageChrome";
import { StatusBadge } from "@/components/shared/StatusBadge";

const LABELS = { "document.uploaded": "Document uploaded", "compliance.document_added": "Compliance evidence recorded", "compliance.document_updated": "Compliance evidence replaced",
  "supplier.created": "Supplier profile created", "supplier.updated": "Supplier profile updated", "property.created": "Property added", "property.updated": "Property updated",
  "property_unit.created": "Unit added", "submission.submitted": "Onboarding submission sent for review", "submission.approved": "Submission approved by Jigsaw", "submission.rejected": "Submission returned by Jigsaw",
  "supplier_offer.submitted": "Offer submitted", "document.access_granted": "Document opened" };

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "none");

// Previous vs current evidence values recorded on the audit row itself (the compliance row is overwritten in place).
function ChangeDiff({ change, typeNames }) {
  const prev = change.previous, cur = change.current || {};
  const typeName = typeNames?.[change.document_type_id];
  return (
    <dl data-testid="audit-change-diff" className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 text-[12px] sm:grid-cols-3">
      {typeName && <div className="sm:col-span-3 text-jigsaw-navy">{typeName}</div>}
      <div><dt className="text-jigsaw-muted">Status</dt><dd className="flex items-center gap-1">{prev ? <><StatusBadge status={prev.status} /><span className="text-jigsaw-muted">→</span></> : null}<StatusBadge status={cur.status} /></dd></div>
      <div><dt className="text-jigsaw-muted">Expiry</dt><dd className="font-mono-ref text-jigsaw-navy">{prev ? `${fmtDate(prev.expires_at)} → ` : ""}{fmtDate(cur.expires_at)}</dd></div>
      <div><dt className="text-jigsaw-muted">File</dt><dd className="text-jigsaw-navy">{!prev ? (cur.document_id ? "Attached" : "None") : prev.document_id === cur.document_id ? "Unchanged" : cur.document_id ? "Replaced" : "Removed"}</dd></div>
    </dl>
  );
}

// Reads existing scoped audit rows only; the empty state is real, nothing is synthesised.
export function AuditList({ endpoint, title, subtitle, testId, emptyText, limit = 20, typeNames, refreshKey = 0 }) {
  const [state, setState] = useState({ loading: true, items: [] });
  useEffect(() => { api.get(endpoint, { params: { limit } }).then((r) => setState({ loading: false, items: r.data.items })).catch(() => setState({ loading: false, items: [], error: true })); }, [endpoint, limit, refreshKey]);
  return (
    <SectionCard title={title} subtitle={subtitle} testId={testId} bodyClassName="px-5 pb-4">
      {state.loading && <p className="text-sm text-jigsaw-muted">Loading…</p>}
      {state.error && <p data-testid={`${testId}-error`} className="text-sm text-jigsaw-danger">Could not load history.</p>}
      {!state.loading && !state.error && state.items.length === 0 && <p data-testid={`${testId}-empty`} className="text-sm text-jigsaw-muted">{emptyText}</p>}
      {state.items.length > 0 && (
        <ol className="divide-y divide-jigsaw-border">
          {state.items.map((a) => (
            <li key={a.id} data-testid={`${testId}-row`} data-action={a.action} className="py-2 text-[13px]">
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-jigsaw-navy">{LABELS[a.action] || a.action.replace(/[._]/g, " ")}</span>
                <span className="font-mono-ref shrink-0 text-[12px] text-jigsaw-muted">{new Date(a.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              {a.change && <ChangeDiff change={a.change} typeNames={typeNames} />}
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  );
}
